import React, { useEffect } from 'react';
import { Linkedin } from 'lucide-react';
import { PUBLIC_HEADER_TAB_STORAGE_KEY } from './PublicHeader';
import { AtelierNav, AtelierFooter, ATELIER_SCROLL_TARGET_KEY } from './landing/atelier/AtelierChrome';
import './landing/atelier/atelier.css';

const WHAT_WE_DO = [
  {
    title: 'Digital Fabric Catalogues',
    desc: 'Turn physical fabric inventory into searchable, shareable digital catalogues.',
  },
  {
    title: 'AI Fashion Design Studio',
    desc: 'Visualise fabrics on garments, silhouettes, and fashion concepts using AI.',
  },
  {
    title: 'Moodboard & Collection Planning',
    desc: 'Create fashion stories, collections, and design directions in a more visual way.',
  },
  {
    title: 'Fashion Sourcing Discovery',
    desc: 'Help buyers, designers, and manufacturers discover relevant fabrics and design possibilities.',
  },
  {
    title: 'AI as the Glue',
    desc: 'We use AI to connect scattered workflows across design, cataloguing, visualisation, and sourcing.',
  },
];

const COFOUNDERS = [
  {
    name: 'Alok Srivastava',
    role: 'Co-Founder',
    imageSrc: '/Alok.jpeg',
    imagePosition: '38% 22%',
    linkedin: 'https://www.linkedin.com/in/alok-srivastava5590/',
    bio: 'BIT Mesra Computer Science alumnus and engineering leader with 16 years of experience building AI-powered platforms at Microsoft, Vista, and Naukri.com.',
  },
  {
    name: 'Shubham Jain',
    role: 'Co-Founder',
    imageSrc: '/shubham.png',
    imagePosition: 'center',
    linkedin: 'https://www.linkedin.com/in/i-shubham-jain/',
    bio: 'DTU alumnus and second-generation textile entrepreneur with 5+ years as a fabric distributor, having directly supplied to brands like Myntra, Shein, and Reliance.',
  },
];

const AboutUsPage = ({ setActiveView, onSupportClick }) => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  const goHome = () => setActiveView('home');
  const goHomeSection = (id) => () => {
    try { window.sessionStorage.setItem(ATELIER_SCROLL_TARGET_KEY, id); } catch (_) {}
    setActiveView('home');
  };
  const goPricing = () => {
    try { window.sessionStorage.setItem(PUBLIC_HEADER_TAB_STORAGE_KEY, 'pricing'); } catch (_) {}
    setActiveView('home');
  };
  const goLogin = () => setActiveView('login');

  const navLinks = [
    ['Fabric library', goHomeSection('library')],
    ['AI Studio', goHomeSection('studio')],
    ['Catalogues', goHomeSection('catalogues')],
    ['For suppliers', goHomeSection('suppliers')],
    ['About', () => window.scrollTo({ top: 0, behavior: 'smooth' }), true],
    ['Pricing', goPricing],
  ];

  return (
    <div className="atl-root">
      <AtelierNav links={navLinks} onLogo={goHome} onSignIn={goLogin} onStartDesigning={goLogin} />

      <header className="atl-about-hero atl-wrap">
        <button type="button" className="atl-link atl-back" onClick={goHome}>← Back to home</button>
        <div className="atl-pill"><span className="atl-dot" />About Fashdeck</div>
        <h1>Digitalising fashion design &amp; sourcing.</h1>
        <p className="atl-about-lead">
          Fashdeck is building an AI-powered platform to digitalise the fashion design and sourcing industry.
        </p>
      </header>

      <section className="atl-section atl-wrap">
        <div className="atl-about-story">
          <div className="atl-eyebrow">Our story</div>
          <div className="atl-about-copy">
            <p>
              Fashion today moves fast, but the process of discovering fabrics, visualising designs, creating
              collections, and connecting with suppliers is still largely manual, fragmented, and time-consuming.
              Fashdeck brings these workflows together into one intelligent platform where fabrics, designs,
              silhouettes, moodboards, and sourcing decisions can live digitally.
            </p>
            <p>
              Our mission is to make fashion creation faster, smarter, and more connected by using AI as the glue
              between fabric sellers, designers, garment manufacturers, and fashion brands.
            </p>
            <p>
              With Fashdeck, fabric sellers can build digital fabric catalogues, designers can visualise fabrics on
              real garments, and manufacturers can showcase endless design possibilities without creating physical
              samples for every idea. Our AI Fashion Studio helps users turn fabric images into realistic fashion
              concepts, experiment with silhouettes, build moodboards, and plan collections in minutes.
            </p>
            <p>
              We believe the future of fashion will be more visual, more collaborative, and more data-driven.
              Fashdeck is here to make that future accessible to every fabric seller, designer, and manufacturer.
            </p>
          </div>
        </div>
      </section>

      <section className="atl-section atl-wrap">
        <div className="atl-about-pair">
          <div className="atl-about-card">
            <div className="atl-eyebrow">Mission</div>
            <h2>Our mission</h2>
            <p>
              To digitalise the fashion design and sourcing ecosystem by combining fabric discovery, design
              visualisation, cataloguing, and AI-powered creativity into one connected platform.
            </p>
          </div>
          <div className="atl-about-card atl-about-card--gold">
            <div className="atl-eyebrow">Vision</div>
            <h2>Our vision</h2>
            <p>
              To become the digital infrastructure for the fashion industry, where every fabric can be discovered,
              every design can be visualised, and every sourcing decision can be made faster with the power of AI.
            </p>
          </div>
        </div>
      </section>

      <section className="atl-section atl-wrap">
        <div className="atl-section-head">
          <div>
            <div className="atl-eyebrow">What we do</div>
            <h2 className="atl-h2">From imagination to execution, faster.</h2>
          </div>
          <p className="atl-lede">Fashdeck helps the fashion industry move from imagination to execution faster.</p>
        </div>
        <div className="atl-about-grid">
          {WHAT_WE_DO.map(({ title, desc }, i) => (
            <div key={title} className="atl-about-card">
              <div className="atl-eyebrow">{String(i + 1).padStart(2, '0')}</div>
              <h3>{title}</h3>
              <p>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="atl-section atl-wrap">
        <div className="atl-section-head">
          <div>
            <div className="atl-eyebrow">Co-founders</div>
            <h2 className="atl-h2">Building the future of fashion technology.</h2>
          </div>
        </div>
        <div className="atl-about-pair">
          {COFOUNDERS.map(({ name, role, imageSrc, imagePosition, linkedin, bio }) => (
            <article key={name} className="atl-about-card atl-founder">
              <div className="atl-founder-head">
                <img src={imageSrc} alt={name} style={{ objectPosition: imagePosition }} loading="lazy" />
                <div>
                  <h3>{name}</h3>
                  <div className="atl-founder-role">{role}</div>
                </div>
              </div>
              <p>{bio}</p>
              <a
                className="atl-founder-link"
                href={linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${name} on LinkedIn`}
              >
                <Linkedin size={16} aria-hidden="true" />
                LinkedIn
              </a>
            </article>
          ))}
        </div>
      </section>

      <section className="atl-final atl-wrap">
        <h2>Your next collection starts with a swatch.</h2>
        <button type="button" className="atl-link atl-btn atl-btn--xl" style={{ marginTop: 36 }} onClick={goLogin}>
          Start designing free
        </button>
      </section>

      <AtelierFooter
        onPricing={goPricing}
        onHelp={onSupportClick}
        onPrivacy={() => setActiveView('privacy-policy')}
      />
    </div>
  );
};

export default AboutUsPage;
